import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Bell, BellRing, Check, CalendarDays, CreditCard, XCircle, Clock, Users, Loader2, CheckCheck, ArrowLeft, Settings } from "lucide-react";
import { isPushSupported, getPermissionState, subscribeToPush, unsubscribeFromPush, isSubscribed } from "@/lib/pushNotifications";

const typeIcons = {
  BOOKING_CREATED: CalendarDays, BOOKING_CONFIRMED: Check, BOOKING_CANCELLED: XCircle,
  CASH_CONFIRMED: CreditCard, BOOKING_REMINDER: Clock, NEW_BOOKING_PROVIDER: Users,
};
const typeColors = {
  BOOKING_CREATED: "text-blue-500", BOOKING_CONFIRMED: "text-lime-volt", BOOKING_CANCELLED: "text-red-500",
  CASH_CONFIRMED: "text-green-500", BOOKING_REMINDER: "text-amber-500", NEW_BOOKING_PROVIDER: "text-purple-500",
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    api.get("/notifications", { params: { limit: 50 } }).then(r => { setNotifications(r.data); setLoading(false); }).catch(() => setLoading(false));
    isSubscribed().then(setPushEnabled);
  }, []);

  const markRead = async (nid) => {
    await api.put(`/notifications/${nid}/read`);
    setNotifications(n => n.map(x => x.notification_id === nid ? { ...x, is_read: true } : x));
  };

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    setNotifications(n => n.map(x => ({ ...x, is_read: true })));
    toast.success("All notifications marked as read");
  };

  const togglePush = async (enabled) => {
    setPushLoading(true);
    try {
      if (enabled) {
        const sub = await subscribeToPush();
        if (sub) { setPushEnabled(true); toast.success("Push notifications enabled!"); }
        else { toast.error("Permission denied or not supported"); }
      } else {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success("Push notifications disabled");
      }
    } catch { toast.error("Failed to update push settings"); }
    setPushLoading(false);
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="notifications-page">
      <button data-testid="back-from-notifs" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Bell className="w-7 h-7 text-court-orange" />Notifications
          </h1>
          {unreadCount > 0 && <p className="text-sm text-muted-foreground mt-1">{unreadCount} unread</p>}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button data-testid="mark-all-read-page" variant="outline" size="sm" onClick={markAllRead} className="rounded-full border-2 text-xs font-heading uppercase">
              <CheckCheck className="w-3 h-3 mr-1" />Read All
            </Button>
          )}
          <Button data-testid="push-settings-btn" variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)} className="rounded-full border-2 text-xs font-heading uppercase">
            <Settings className="w-3 h-3 mr-1" />Settings
          </Button>
        </div>
      </div>

      {/* Push Settings Panel */}
      {showSettings && (
        <div className="bg-white border-2 border-slate-100 p-5 mb-6" data-testid="push-settings-panel">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-2">
            <BellRing className="w-4 h-4" />Push Notification Settings
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Browser Push Notifications</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {!isPushSupported() ? "Not supported in this browser" :
                 getPermissionState() === "denied" ? "Blocked - enable in browser settings" :
                 pushEnabled ? "Receiving push notifications" : "Enable to get notified even when the app is closed"}
              </p>
            </div>
            <Switch data-testid="push-toggle" checked={pushEnabled} onCheckedChange={togglePush}
              disabled={pushLoading || !isPushSupported() || getPermissionState() === "denied"} />
          </div>
          <Separator className="my-4" />
          <p className="text-xs text-muted-foreground">You'll be notified about: booking confirmations, cancellations, payment updates, and game-time reminders (1 hour before).</p>
        </div>
      )}

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-slate-200">
          <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No notifications yet</p>
          <p className="text-sm text-muted-foreground mt-1">You'll see booking updates and reminders here</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notifications.map(n => {
            const Icon = typeIcons[n.type] || Bell;
            const color = typeColors[n.type] || "text-muted-foreground";
            return (
              <div key={n.notification_id} data-testid={`notif-item-${n.notification_id}`}
                onClick={() => !n.is_read && markRead(n.notification_id)}
                className={`flex gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors ${!n.is_read ? 'bg-court-orange/[0.04] border-l-4 border-court-orange' : 'bg-white border-l-4 border-transparent'}`}>
                <div className={`mt-0.5 shrink-0 ${color}`}><Icon className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${!n.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>{n.title}</p>
                    <Badge variant="outline" className="text-[8px] font-heading uppercase tracking-wider">{n.type?.replace(/_/g, " ")}</Badge>
                    {!n.is_read && <span className="w-2 h-2 bg-court-orange rounded-full shrink-0" />}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-2">{formatTime(n.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
